import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { FiSave, FiRefreshCw } from "react-icons/fi";
import {
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  TextArea,
} from "../shared/StyledComponents";
import footerService, { FooterSettings } from "../../services/footerService";

// Interfaces para los datos del footer
interface ContactInfo {
  phone: string;
  email: string;
  address: string;
}

interface SocialLinks {
  facebook: string;
  instagram: string;
  tiktok: string;
}

interface FooterData {
  contact: ContactInfo;
  social: SocialLinks;
  copyright: string;
  developedBy: string;
}

const TabContainer = styled.div`
  padding: 1.5rem;
  max-width: 1200px;

`;

const SectionCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
`;

const SectionTitle = styled.h3`
  color: #242a5b;
  font-size: 1.2rem;
  margin-bottom: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &::before {
    content: "";
    width: 4px;
    height: 20px;
    background: linear-gradient(135deg, var(--primary-orange), #FDBF37);
    border-radius: 2px;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const SaveButton = styled(Button)`
  background: linear-gradient(135deg, #28a745, #20c997);
  color: white;
  padding: 0.75rem 2rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  
  &:hover {
    background: linear-gradient(135deg, #218838, #1ea085);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
  }
`;

const ResetButton = styled(Button)`
  background: linear-gradient(135deg, #6c757d, #5a6268);
  color: white;
  padding: 0.75rem 2rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  margin-left: 1rem;
  
  &:hover {
    background: linear-gradient(135deg, #5a6268, #495057);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(108, 117, 125, 0.3);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-top: 2rem;
`;

const FooterTab: React.FC = () => {
  const [footerData, setFooterData] = useState<FooterData>({
    contact: {
      phone: "",
      email: "",
      address: ""
    },
    social: {
      facebook: "",
      instagram: "",
      tiktok: ""
    },
    copyright: "",
    developedBy: ""
  });

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Cargar datos del footer desde el backend
  useEffect(() => {
    const loadFooterData = async () => {
      try {
        setInitialLoading(true);
        const settings = await footerService.getFooterSettings();
        
        setFooterData({
          contact: {
            phone: settings.contact_phone || "Lunes a Domingo de 8:00 a.m. a 8:00 p.m.",
            email: settings.contact_email || "servicios@datax.com",
            address: settings.contact_address || "Av. Centenario #220"
          },
          social: {
            facebook: settings.social_facebook || "https://www.facebook.com/vargashomecenter",
            instagram: settings.social_instagram || "https://www.instagram.com/vargashomecenter/",
            tiktok: settings.social_tiktok || "https://www.tiktok.com/@vargas.homecenter"
          },
          copyright: settings.copyright_text || "© 2025 DataX. Todos los derechos reservados.",
          developedBy: "Desarrollado con ❤️ por DataX para nuestros clientes"
        });
      } catch (error) {
        console.error("Error al cargar configuraciones del footer:", error);
        toast.error("Error al cargar la configuración del footer");
        // Mantener valores por defecto en caso de error
        setFooterData({
          contact: {
            phone: "Lunes a Domingo de 8:00 a.m. a 8:00 p.m.",
            email: "servicios@datax.com",
            address: "Av. Centenario #220"
          },
          social: {
            facebook: "https://www.facebook.com/vargashomecenter",
            instagram: "https://www.instagram.com/vargashomecenter/",
            tiktok: "https://www.tiktok.com/@vargas.homecenter"
          },
          copyright: "© 2025 DataX. Todos los derechos reservados.",
          developedBy: "Desarrollado con ❤️ por DataX para nuestros clientes"
        });
      } finally {
        setInitialLoading(false);
      }
    };

    loadFooterData();
  }, []);

  const handleInputChange = (section: keyof FooterData, field: string, value: string) => {
    setFooterData(prev => {
      if (section === 'contact' || section === 'social') {
        return {
          ...prev,
          [section]: {
            ...prev[section],
            [field]: value
          }
        };
      } else {
        return {
          ...prev,
          [section]: value
        };
      }
    });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Preparar datos para enviar al backend
      const settingsToSave = {
        contact_address: footerData.contact.address,
        contact_phone: footerData.contact.phone,
        contact_email: footerData.contact.email,
        social_facebook: footerData.social.facebook,
        social_instagram: footerData.social.instagram,
        social_tiktok: footerData.social.tiktok,
        copyright_text: footerData.copyright
      };

      await footerService.updateFooterSettings(settingsToSave);
      toast.success("Configuración del footer guardada exitosamente");
    } catch (error) {
      console.error("Error al guardar:", error);
      toast.error("Error al guardar la configuración del footer");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    try {
      setLoading(true);
      // Recargar datos desde el backend
      const settings = await footerService.getFooterSettings();
      
      setFooterData({
        contact: {
          phone: settings.contact_phone || "Lunes a Domingo de 8:00 a.m. a 8:00 p.m.",
          email: settings.contact_email || "servicios@datax.com",
          address: settings.contact_address || "Av. Centenario #220"
        },
        social: {
          facebook: settings.social_facebook || "https://www.facebook.com/vargashomecenter",
          instagram: settings.social_instagram || "https://www.instagram.com/vargashomecenter/",
          tiktok: settings.social_tiktok || "https://www.tiktok.com/@vargas.homecenter"
        },
        copyright: settings.copyright_text || "© 2025 DataX. Todos los derechos reservados.",
        developedBy: "Desarrollado con ❤️ por DataX para nuestros clientes"
      });
      
      toast.info("Configuración restablecida desde la base de datos");
    } catch (error) {
      console.error("Error al restablecer:", error);
      toast.error("Error al restablecer la configuración");
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <TabContainer>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p>Cargando configuración del footer...</p>
        </div>
      </TabContainer>
    );
  }

  return (
    <TabContainer>
      <Form>
        {/* Sección de Contacto */}
        <SectionCard>
          <SectionTitle>📞 Información de Contacto</SectionTitle>
          <FormRow>
            <FormGroup>
              <Label>Horario de Atención</Label>
              <Input
                type="text"
                value={footerData.contact.phone}
                onChange={(e) => handleInputChange('contact', 'phone', e.target.value)}
                placeholder="Ej: Lunes a Domingo de 8:00 a.m. a 8:00 p.m."
              />
            </FormGroup>
          </FormRow>
          <FormRow>
            <FormGroup>
              <Label>Email de Contacto</Label>
              <Input
                type="email"
                value={footerData.contact.email}
                onChange={(e) => handleInputChange('contact', 'email', e.target.value)}
                placeholder="Ej: servicios@datax.com"
              />
            </FormGroup>
            <FormGroup>
              <Label>Dirección</Label>
              <Input
                type="text"
                value={footerData.contact.address}
                onChange={(e) => handleInputChange('contact', 'address', e.target.value)}
                placeholder="Ej: Av. Centenario #220"
              />
            </FormGroup>
          </FormRow>

        </SectionCard>

        {/* Sección de Redes Sociales */}
        <SectionCard>
          <SectionTitle>🌐 Redes Sociales</SectionTitle>
          <FormRow>
            <FormGroup>
              <Label>Facebook URL</Label>
              <Input
                type="url"
                value={footerData.social.facebook}
                onChange={(e) => handleInputChange('social', 'facebook', e.target.value)}
                placeholder="https://www.facebook.com/..."
              />
            </FormGroup>
            <FormGroup>
              <Label>Instagram URL</Label>
              <Input
                type="url"
                value={footerData.social.instagram}
                onChange={(e) => handleInputChange('social', 'instagram', e.target.value)}
                placeholder="https://www.instagram.com/..."
              />
            </FormGroup>
          </FormRow>
          <FormRow>
            <FormGroup>
              <Label>TikTok URL</Label>
              <Input
                type="url"
                value={footerData.social.tiktok}
                onChange={(e) => handleInputChange('social', 'tiktok', e.target.value)}
                placeholder="https://www.tiktok.com/..."
              />
            </FormGroup>
          </FormRow>
        </SectionCard>

        {/* Sección de Copyright */}
        <SectionCard>
          <SectionTitle>©️ Información de Copyright</SectionTitle>
          <FormRow>
            <FormGroup>
              <Label>Texto de Copyright</Label>
              <Input
                type="text"
                value={footerData.copyright}
                onChange={(e) => handleInputChange('copyright', '', e.target.value)}
                placeholder="© 2025 DataX. Todos los derechos reservados."
              />
            </FormGroup>
            <FormGroup>
              <Label>Texto de Desarrollado por</Label>
              <Input
                type="text"
                value={footerData.developedBy}
                onChange={(e) => handleInputChange('developedBy', '', e.target.value)}
                placeholder="Desarrollado con ❤️ por DataX para nuestros clientes"
              />
            </FormGroup>
          </FormRow>
        </SectionCard>

        <ButtonGroup>
          <SaveButton onClick={handleSave} disabled={loading}>
            <FiSave />
            {loading ? "Guardando..." : "Guardar Cambios"}
          </SaveButton>
          <ResetButton onClick={handleReset} disabled={loading}>
            <FiRefreshCw />
            Restablecer
          </ResetButton>
        </ButtonGroup>
      </Form>
    </TabContainer>
  );
};

export default FooterTab;